import * as React from "react";

export type LoadingState = "loading" | "success" | "error";

interface InnerSpinnerState {
  readonly renderState: LoadingState | undefined;
  readonly startAnimation: boolean;
}

type SpinnerAction =
  | { type: "stateChanged"; state: LoadingState | undefined }
  | { type: "animationEnded" };

const reduce: React.Reducer<InnerSpinnerState, SpinnerAction> = (
  current,
  action,
) => {
  switch (action.type) {
    case "stateChanged": {
      if (action.state !== "success") {
        if (current.renderState === action.state && !current.startAnimation) {
          return current;
        }

        return { renderState: action.state, startAnimation: false };
      }

      // The checkmark animates a spinner away, so there has to be one on
      // screen. With no loading or error before it, settle on success directly.
      if (
        current.renderState !== "loading" &&
        current.renderState !== "error"
      ) {
        if (current.renderState === "success" && !current.startAnimation) {
          return current;
        }

        return { renderState: "success", startAnimation: false };
      }

      if (current.startAnimation) {
        return current;
      }

      return { ...current, startAnimation: true };
    }
    case "animationEnded": {
      // A checkmark finishing when nothing is animating belongs to a success
      // the caller has moved past; honouring it would hide the next load's
      // spinner.
      if (!current.startAnimation) {
        return current;
      }

      return { renderState: "success", startAnimation: false };
    }
  }
};

interface SpinnerState extends InnerSpinnerState {
  readonly onSuccessAnimationEnd: () => void;
}

export function useSuccessSpinnerState(
  state: LoadingState | undefined,
): SpinnerState {
  const [{ renderState, startAnimation }, dispatch] = React.useReducer(
    reduce,
    state,
    (renderState) => ({
      renderState,
      startAnimation: false,
    }),
  );

  React.useEffect(() => {
    dispatch({ type: "stateChanged", state });
  }, [state]);

  return React.useMemo<SpinnerState>(() => {
    const onSuccessAnimationEnd = () => {
      dispatch({ type: "animationEnded" });
    };

    return { renderState, startAnimation, onSuccessAnimationEnd };
  }, [renderState, startAnimation]);
}
